// src/pages/resume/EditorPage.js — single-page split-view editor (phase 1).
// Data loading / finish / rename / delete moved verbatim from EditorLayout.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UpdateResumeModal from '../../components/resume/UpdateResumeModal';
import ConfirmModal from '../../components/common/ConfirmModal';
import FeedbackModal from '../../components/common/FeedbackModal';
import { useResume } from '../../context/ResumeContext';
import { EDITOR_SECTIONS, deriveCompleteness } from '../../lib/resumeCompleteness';
import useScrollSpy from '../../hooks/useScrollSpy';
import SectionRail from '../../components/resume/editor/SectionRail';
import PreviewPanel from '../../components/resume/editor/PreviewPanel';
import { EditorSaveProvider } from '../../context/EditorSaveContext';
import { useLatexEngine } from '../../latex/useLatexEngine';
import { generateTex } from '../../latex/jakeTemplate';
import { buildTexData } from '../../latex/buildTexData';
import Contact from './contact';
import Experience from './experience';
import Education from './education';
import Certifications from './certifications';
import Awards from './awards';
import Skills from './skills';
import Projects from './projects';
import Summary from './summary';

const SECTION_COMPONENTS = {
    contact: Contact, experience: Experience, education: Education,
    certifications: Certifications, awards: Awards, skills: Skills,
    projects: Projects, summary: Summary,
};

const SECTION_IDS = EDITOR_SECTIONS.map(s => s.key);

const EditorPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { resumeId } = useParams();

    // --- Registry for autosave flush functions ---
    const flushersRef = useRef(new Map());
    const saveRegistry = useMemo(() => ({
        register: (key, fn) => { flushersRef.current.set(key, fn); },
        unregister: (key) => { flushersRef.current.delete(key); },
    }), []);

    // --- 1. Get Setters and Data from Context ---
    const resumeData = useResume();
    const {
        setContact, setSummary, setSkills, setExperiences,
        setEducations, setAwards, setCertifications, setProjects
    } = resumeData;

    // State
    const [resumeName, setResumeName] = useState(location.state?.resumeName || 'Loading...');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isNewAi] = useState(location.state?.isNewAiResume || false); // Flag to skip fetch if just created

    // Modals
    const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { }, confirmText: 'Confirm' });
    const [feedbackModalState, setFeedbackModalState] = useState({ isOpen: false, title: '', message: '', isError: false });

    // --- 2. DATA LOADING ---
    useEffect(() => {
        const fetchResumeData = async () => {
            if (isNewAi) {
                console.log("Skipping fetch: New AI Resume detected.");
                return;
            }

            try {
                const response = await fetch(`https://renaisons.com/api/get_resume_details.php?resume_id=${resumeId}`, {
                    credentials: 'include'
                });
                const result = await response.json();

                if (result.status === 'success' && result.data) {
                    const dbData = result.data;

                    // A. Set Resume Name (Title)
                    setResumeName(dbData.resume_name || 'Untitled Resume');

                    // B. Map Contact Info
                    const rawContact = dbData.contact_info || {};
                    const mappedContact = {
                        fullName: rawContact.full_name || rawContact.fullName || '',
                        email: rawContact.email || '',
                        phone: rawContact.phone || '',
                        linkedin: rawContact.linkedin || '',
                        website: rawContact.website || '',
                        city: rawContact.city || '',
                        state: rawContact.state || '',
                        country: rawContact.country || ''
                    };
                    setContact(mappedContact);

                    // C. Map Simple Strings (Summary & Skills) - FIX: Safely check multiple API formats
                    const fetchedSummary = dbData.summary?.summaries_description || dbData.summaries?.[0]?.summaries_description || dbData.resume_summaries?.[0]?.summaries_description;
                    if (fetchedSummary !== undefined) {
                        setSummary(fetchedSummary);
                    }

                    const fetchedSkills = dbData.skills?.skills_description || dbData.skills?.[0]?.skills_description;
                    if (fetchedSkills !== undefined) {
                        setSkills(fetchedSkills);
                    }

                    // D. Map Arrays
                    setExperiences(dbData.experiences || []);
                    setEducations(dbData.educations || []);
                    setProjects(dbData.projects || []);
                    setAwards(dbData.awards || []);
                    setCertifications(dbData.certifications || []);

                } else {
                    console.error("Failed to load resume data:", result.message);
                }
            } catch (error) {
                console.error("Network error fetching resume:", error);
            }
        };

        if (resumeId) {
            fetchResumeData();
        }
    }, [resumeId, isNewAi, setContact, setSummary, setSkills, setExperiences, setEducations, setProjects, setAwards, setCertifications]);


    // --- 3. SAVE FUNCTIONALITY ---
    const handleFinish = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const entries = [...flushersRef.current.entries()];
            const results = await Promise.all(entries.map(async ([key, flushFn]) => {
                try { const r = await flushFn(); return { key, ok: r?.ok !== false }; }
                catch (e) { return { key, ok: false }; }
            }));
            const failedKeys = results.filter(r => !r.ok).map(r => r.key);
            if (failedKeys.length > 0) {
                const labels = EDITOR_SECTIONS
                    .filter(s => failedKeys.includes(s.key))
                    .map(s => s.label);
                setFeedbackModalState({
                    isOpen: true,
                    title: 'Some changes did not save',
                    message: `Please check these sections: ${labels.join(', ')}`,
                    isError: true,
                });
                return;
            }
            navigate(`/resume/${resumeId}/final`, { state: { resumeName } });
        } finally {
            setIsSaving(false);
        }
    };

    // --- beforeunload guard: warn if any autosave section is pending ---
    useEffect(() => {
        const handler = (e) => {
            const anyPending = window.__editorHasPendingSaves === true;
            if (anyPending) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // --- 4. RENAME & DELETE HANDLERS ---
    const handleUpdateName = (newName) => {
        setResumeName(newName);
        setIsEditModalOpen(false);
    };

    const handleDeleteClick = () => {
        setModalState({
            isOpen: true,
            title: 'Delete Resume',
            message: `Are you sure you want to delete "${resumeName}"? This cannot be undone.`,
            confirmText: 'Delete',
            onConfirm: performDelete
        });
    };

    const performDelete = async () => {
        setModalState({ isOpen: false });
        try {
            const res = await fetch('https://renaisons.com/api/delete_resume.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ resume_id: resumeId }),
                credentials: 'include'
            });
            const data = await res.json();
            if (data.status === 'success') {
                navigate('/resume');
            } else {
                setFeedbackModalState({ isOpen: true, title: 'Error', message: data.message || 'Delete failed', isError: true });
            }
        } catch (e) {
            setFeedbackModalState({ isOpen: true, title: 'Error', message: 'Network error', isError: true });
        }
    };

    // --- 5. SPLIT-VIEW STATE ---
    const completeness = useMemo(() => deriveCompleteness(resumeData), [resumeData]);
    const activeId = useScrollSpy(SECTION_IDS);

    // --- 6. PANEL TAB (Outline / Preview) with localStorage persistence ---
    const [panelTab, setPanelTabState] = useState(() => {
        try {
            const t = window.localStorage.getItem('editorPanelTab');
            return t === 'preview' ? 'preview' : 'outline';
        } catch (_) { return 'outline'; }
    });
    const setPanelTab = (t) => {
        setPanelTabState(t);
        try { window.localStorage.setItem('editorPanelTab', t); } catch (_) { /* private mode etc. */ }
    };
    const previewActive = panelTab === 'preview';
    const { compile, ready, compiling, initializing, error: engineError, retryInit } =
        useLatexEngine({ enabled: previewActive });

    const [previewPdf, setPreviewPdf] = useState(null);
    const [previewCompileError, setPreviewCompileError] = useState(false);
    const lastTexRef = useRef('');

    const texSource = useMemo(
        () => (previewActive && ready) ? generateTex(buildTexData(resumeData)) : null,
        [previewActive, ready, resumeData]);

    useEffect(() => {
        if (!texSource || texSource === lastTexRef.current) return undefined;
        // First compile after activation fires immediately; edits debounce 2 s.
        const delay = lastTexRef.current === '' ? 0 : 2000;
        const t = setTimeout(async () => {
            lastTexRef.current = texSource;
            const r = await compile(texSource);
            if (r.status === 0 && r.pdf) { setPreviewPdf(r.pdf); setPreviewCompileError(false); }
            else { setPreviewCompileError(true); }
        }, delay);
        return () => clearTimeout(t);
    }, [texSource, compile]);

    const scrollToSection = (key) => {
        const el = document.getElementById(key);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Legacy deep links: /resume/:id#experience lands on that card.
    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) el.scrollIntoView({ block: 'start' });
        }
    }, [location.hash]);

    return (
        <EditorSaveProvider value={saveRegistry}>
            <UpdateResumeModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleUpdateName} currentName={resumeName} />
            <div className="text-white min-h-screen p-4 md:p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Sticky header */}
                    <header className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#05070b]/90 backdrop-blur-sm border-b border-white/10 mb-6 flex flex-wrap items-center justify-between gap-3">
                        <div className="relative">
                            {/* Resume-name dropdown */}
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center space-x-2 rounded-lg border border-white/10 bg-[#0b0e14]/60 px-4 py-2 font-semibold hover:bg-white/5"
                            >
                                <span>{resumeName}</span>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </button>
                            {isDropdownOpen && (
                                <div className="absolute top-full mt-2 w-48 rounded-lg border border-white/10 bg-[#0b0e14] shadow-lg z-30">
                                    <ul>
                                        <li><button onClick={() => { setIsEditModalOpen(true); setIsDropdownOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-blue-600">Rename</button></li>
                                        <li><button onClick={handleDeleteClick} className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:text-white">Delete</button></li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        <button onClick={handleFinish} disabled={isSaving}
                            className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg disabled:opacity-50 transition-colors">
                            {isSaving ? 'Saving...' : 'Finish Up & Preview'}
                        </button>
                    </header>

                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                        <SectionRail sections={completeness} activeId={activeId} onNavigate={scrollToSection} />

                        <main className="flex-1 min-w-0 space-y-6">
                            {EDITOR_SECTIONS.map(({ key, label }) => {
                                const Form = SECTION_COMPONENTS[key];
                                return (
                                    <section key={key} id={key} className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5 scroll-mt-36 lg:scroll-mt-28">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                                            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{label}</h2>
                                        </div>
                                        <Form />
                                    </section>
                                );
                            })}
                        </main>

                        <PreviewPanel
                            tab={panelTab}
                            onTabChange={setPanelTab}
                            sections={completeness}
                            onNavigate={scrollToSection}
                            preview={{ initializing, ready, compiling, engineError, pdf: previewPdf, compileError: previewCompileError, onRetryInit: retryInit }}
                        />
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ConfirmModal
                isOpen={modalState.isOpen}
                onClose={() => setModalState({ ...modalState, isOpen: false })}
                onConfirm={modalState.onConfirm}
                title={modalState.title}
                message={modalState.message}
                confirmText={modalState.confirmText}
            />
            {feedbackModalState.isOpen && (
                <FeedbackModal
                    title={feedbackModalState.title}
                    message={feedbackModalState.message}
                    isError={feedbackModalState.isError}
                    onClose={() => setFeedbackModalState({ ...feedbackModalState, isOpen: false })}
                />
            )}
        </EditorSaveProvider>
    );
};

export default EditorPage;
